/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useMemo, useEffect } from 'react';
import { Table, Dropdown, MenuProps, Button, Modal, Input, Tooltip } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { MenuInfo } from 'rc-menu/lib/interface';
import { useDispatch, useSelector } from 'react-redux';
import { CareHomeOption } from 'components/SelectedCareHomes/types';
import dayjs, { Dayjs } from 'dayjs';
import { IconEdit, ExclamationIcon } from '../Elements/CustomSVGIcon';
import './newsUpdatesTable.scss';
import { NewsArticle } from '../../core/news/types';
import { AppDispatch, CombinedStates } from '../../core/types';
import { deleteNews, publishScheduledNews, updateScheduledNews } from '../../core/news/newsApi';
import { clearNewsState } from '../../core/news/newsSlice';
import { Mode } from './types';
import PhonePreview from '../PhonePreview';
import CheckboxDropdown from '../CheckboxDropdown';
import SelectedCareHomes from '../SelectedCareHomes';
import Checkbox from '../Checkbox';
import ScheduleModal from '../ScheduleModal';
import { CareHome } from '../../core/assessment/types';

const { TextArea } = Input;

interface NewsUpdatesTableProps {
  mode: Mode;
  articles: NewsArticle[];
  careHomes: CareHome[];
}

type TableRecord = {
  key: string;
  articleId: number;
  title: string;
  time: string;
  pushNotificationSent: string;
  createdBy: string;
  originalArticle: NewsArticle;
};

const NewsUpdatesTable: React.FC<NewsUpdatesTableProps> = ({ mode, articles, careHomes }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { isFetching, isUpdating, isPublishing, success } = useSelector((state: CombinedStates) => state.news);
    const isScheduled = mode === 'scheduled';
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<NewsArticle | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [editContent, setEditContent] = useState('');
    const [editLink, setEditLink] = useState('');
    const [editLinkLabel, setEditLinkLabel] = useState('');
    const [sendToAll, setSendToAll] = useState(true);
    const [selectedCareHomes, setSelectedCareHomes] = useState<CareHomeOption[]>([]);
    const [pushNotif, setPushNotif] = useState(false);
    const [pushNotifTitle, setPushNotifTitle] = useState('');
    const [pushNotifDescription, setPushNotifDescription] = useState('');
    const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
    const [scheduleDate, setScheduleDate] = useState<Dayjs | null>(null);
    const [scheduleTime, setScheduleTime] = useState<Dayjs | null>(null);
    const [isPublishConfirmVisible, setIsPublishConfirmVisible] = useState(false);

    const tableData = useMemo(() => articles.map((article) => {
        const { id, title, text, media_url, link, link_label, audience, audience_label, publish_at, created_at, push_notification, creator } = article;
        return {
            key: id.toString(),
            articleId: id,
            title,
            time: publish_at || created_at,
            pushNotificationSent: push_notification === 'yes' ? 'Yes' : 'No',
            createdBy: creator?.name || 'Unknown',
            text,
            media_url,
            link,
            link_label,
            audience: audience_label || audience,
            originalArticle: article,
        };
    }), [articles]);

    const handleMenuClick = (e: MenuInfo, record: NewsArticle) => {
        if (e.key === 'view') {
            setSelectedRecord(record);
            if (isScheduled) {
                setEditTitle(record.title);
                setEditContent(record.text);
                setEditLink(record.link || '');
                setEditLinkLabel(record.link_label || '');
                setSendToAll(record.audience === 'care_home_users');
                const formattedCareHomes = (record.care_homes || []).map(home => ({
                    label: home.title,
                    value: home.id.toString(),
                }));
                setSelectedCareHomes(formattedCareHomes);
                setPushNotif(record.push_notification === 'yes');
                setPushNotifTitle(record.notification_title || '');
                setPushNotifDescription(record.notification_description || '');
            }
            setIsViewModalVisible(true);
        } else if (e.key === 'delete') {
            setSelectedRecord(record);
            setIsDeleteModalVisible(true);
        }
    };

    const getMenuItems = (record: NewsArticle): MenuProps['items'] => {
        const items: MenuProps['items'] = [
            {
                key: 'view',
                label: 'View',
                onClick: () => handleMenuClick({ key: 'view' } as MenuInfo, record),
                type: 'item',
            },
        ];
        if (isScheduled) {
            items.push({
                key: 'delete',
                label: 'Delete',
                onClick: () => handleMenuClick({ key: 'delete' } as MenuInfo, record),
                type: 'item',
            });
        }

        return items;
    };

    const scheduledColumns = [
        { title: 'Title', dataIndex: 'title', key: 'title' },
        { title: 'Time Scheduled', dataIndex: 'time', key: 'time' },
        { title: 'Created By', dataIndex: 'createdBy', key: 'createdBy' },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, record: TableRecord) => (
                <Dropdown menu={{ items: getMenuItems(record.originalArticle) || [] }} trigger={['click']} placement="bottomLeft" className="custom-dropdown">
                    <Button type="link" icon={<IconEdit />} />
                </Dropdown>
            ),
        },
    ];

    const publishedColumns = [
        { title: 'Title', dataIndex: 'title', key: 'title' },
        { title: 'Time Posted', dataIndex: 'time', key: 'time' },
        { title: 'Push Notification Sent', dataIndex: 'pushNotificationSent', key: 'pushNotificationSent' },
        { title: 'Created By', dataIndex: 'createdBy', key: 'createdBy' },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, record: TableRecord) => (
                <Dropdown menu={{ items: getMenuItems(record.originalArticle) || [] }} trigger={['click']} placement="bottomLeft" className="custom-dropdown">
                    <Button type="link" icon={<IconEdit />} />
                </Dropdown>
            ),
        },
    ];

    const columns = isScheduled ? scheduledColumns : publishedColumns;
    const heading = isScheduled ? 'Scheduled News Updates' : 'Published News Updates';

    const handleDelete = () => {
        if (selectedRecord) {
            dispatch(deleteNews(selectedRecord.id));
            setIsDeleteModalVisible(false);
            setSelectedRecord(null);
        }
    };

    const handleCareHomeSelect = (values: { label: string; value: string }[]) => {
        setSelectedCareHomes(values);
    };

    const careHomeOptions: CareHomeOption[] = (careHomes ?? []).map(careHome => ({
        label: careHome.title,
        value: careHome.id.toString(),
    }));

    const getAudienceText = (record: NewsArticle) => {
        if (record.audience_label === 'Care Home Users') {
            return 'Sent to All Care Homes';
        }
        return null;
    };

    const handleScheduleForLater = () => {
        if (selectedRecord?.publish_at) {
            try {
                const publishDate = dayjs(selectedRecord.publish_at);
                if (publishDate.isValid()) {
                    setScheduleDate(publishDate);
                    setScheduleTime(publishDate);
                } else {
                    const defaultDate = dayjs().add(1, 'hour');
                    setScheduleDate(defaultDate);
                    setScheduleTime(defaultDate);
                }
            } catch (error) {
                const defaultDate = dayjs().add(1, 'hour');
                setScheduleDate(defaultDate);
                setScheduleTime(defaultDate);
            }
        } else {
            const defaultDate = dayjs().add(1, 'hour');
            setScheduleDate(defaultDate);
            setScheduleTime(defaultDate);
        }
        setScheduleModalVisible(true);
    };

    const handleScheduleSave = () => {
        if (!scheduleDate || !scheduleTime || !selectedRecord) {
            return;
        }

        const dateTimeString = `${scheduleDate.format('YYYY-MM-DD')}T${scheduleTime.format('HH:mm:ss')}`;
        const careHomeIds = selectedCareHomes.map(home => parseInt(home.value, 10));

        const payload = {
            id: selectedRecord.id,
            title: editTitle,
            text: editContent,
            audience: sendToAll ? 'care_home_users' : 'selected_care_homes',
            push_notification: pushNotif,
            send_to_all_care_homes: sendToAll,
            status: 'inactive',
            ...(editLink && { link: editLink }),
            ...(editLinkLabel && { link_label: editLinkLabel }),
            ...(pushNotif && pushNotifTitle && { notification_title: pushNotifTitle }),
            ...(pushNotif && pushNotifDescription && { notification_description: pushNotifDescription }),
            ...(!sendToAll && careHomeIds.length > 0 && { care_home_ids: careHomeIds }),
            publish_at: dateTimeString,
        };

        dispatch(updateScheduledNews(payload));
        setScheduleModalVisible(false);
    };

    const handleScheduleCancel = () => {
        setScheduleModalVisible(false);
    };

    const handlePublishNow = () => {
        setIsPublishConfirmVisible(true);
    };

    const handleConfirmPublish = () => {
        if (!selectedRecord) return;

        const careHomeIds = selectedCareHomes.map(home => parseInt(home.value, 10));

        const payload = {
            id: selectedRecord.id,
            title: editTitle,
            text: editContent,
            audience: sendToAll ? 'care_home_users' : 'selected_care_homes',
            push_notification: pushNotif,
            send_to_all_care_homes: sendToAll,
            ...(editLink && { link: editLink }),
            ...(editLinkLabel && { link_label: editLinkLabel }),
            ...(pushNotif && pushNotifTitle && { notification_title: pushNotifTitle }),
            ...(pushNotif && pushNotifDescription && { notification_description: pushNotifDescription }),
            ...(!sendToAll && careHomeIds.length > 0 && { care_home_ids: careHomeIds }),
        };

        dispatch(publishScheduledNews(payload));
        setIsPublishConfirmVisible(false);
    };

    useEffect(() => {
        if (success) {
            setIsViewModalVisible(false);
            setIsPublishConfirmVisible(false);
            setScheduleModalVisible(false);
            dispatch(clearNewsState());
        }
    }, [success, dispatch]);

    return (
        <div className={`news-updates-table ${(isViewModalVisible || isDeleteModalVisible || scheduleModalVisible || isPublishConfirmVisible) ? 'modal-open' : ''}`}>
            <h3>{heading}</h3>
            <div className="news-updates-table-container">
                <Table
                    columns={columns}
                    dataSource={tableData}
                    pagination={false}
                    rowClassName={(record, index) => (index % 2 === 0 ? 'light-row' : '')}
                    style={{ border: '1px solid #ffffff', borderRadius: '10px' }}
                    rowKey="key"
                    bordered={false} />
            </div>
            <Modal
                open={isViewModalVisible && !!selectedRecord}
                onCancel={() => setIsViewModalVisible(false)}
                footer={null}
                width="100%"
                style={{
                    top: 0,
                    padding: 0,
                    maxWidth: '100%',
                }}
                wrapClassName="fullscreen-modal-wrap"
                className="fullscreen-modal"
                closable
                maskClosable={false}>
                {selectedRecord && (
                    <div className="news-builder">
                        <div className="builder-form">
                            <div className="builder-form-header">
                                <h3>{isScheduled ? 'Edit Scheduled News Update' : 'Published Post'}</h3>
                            </div>
                            <div>
                                <label>Image</label>
                                <div className="image-upload">
                                    {selectedRecord.media_url ? (
                                        <div className="image-preview-container">
                                            <img
                                                src={selectedRecord.media_url}
                                                alt="Preview"
                                                style={{ maxHeight: '80px', maxWidth: '100%', objectFit: 'contain' }} />
                                        </div>
                                    ) : (
                                        <div className="no-image">No image available</div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label>Title</label>
                                <Input
                                    value={isScheduled ? editTitle : selectedRecord.title}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    disabled={!isScheduled} />
                            </div>
                            <div>
                                <label>Content</label>
                                <TextArea
                                    rows={6}
                                    value={isScheduled ? editContent : selectedRecord.text}
                                    onChange={(e) => setEditContent(e.target.value)}
                                    disabled={!isScheduled} />
                            </div>
                            <div>
                                <label>Link</label>
                                <Input
                                    value={isScheduled ? editLink : (selectedRecord.link || 'N/A')}
                                    onChange={(e) => setEditLink(e.target.value)}
                                    placeholder="Enter link URL"
                                    disabled={!isScheduled} />
                            </div>
                            <div>
                                <label>Link Label</label>
                                <Input
                                    value={isScheduled ? editLinkLabel : (selectedRecord.link_label || 'N/A')}
                                    onChange={(e) => setEditLinkLabel(e.target.value)}
                                    placeholder="Enter link label"
                                    disabled={!isScheduled} />
                            </div>
                            {isScheduled && (
                                <>
                                    <div className="checkbox-group">
                                        <div className="checkbox-item">
                                            <Checkbox checked={sendToAll} onChange={() => setSendToAll(!sendToAll)} />
                                            <span>Send to all care homes</span>
                                        </div>
                                        {!sendToAll && (
                                            <div className="care-home-dropdown">
                                                <CheckboxDropdown
                                                    options={careHomeOptions}
                                                    onSelect={handleCareHomeSelect}
                                                    placeholder="Select Care Home(s) to Receive Update"
                                                    styles={{
                                                        border: '1px solid #83ACCF',
                                                        height: '50px',
                                                        borderRadius: '8px',
                                                        fontSize: '16px',
                                                    }}
                                                    values={selectedCareHomes} />
                                                <SelectedCareHomes
                                                    selectedCareHomes={selectedCareHomes}
                                                    onRemove={(careHome) => {
                                                        setSelectedCareHomes(
                                                            selectedCareHomes.filter(home => home.value !== careHome.value),
                                                        );
                                                    }}
                                                    label="Care Homes Selected:"
                                                    containerStyle={{ marginTop: '12px', flexDirection: 'column', alignItems: 'flex-start' }} />
                                            </div>
                                        )}
                                        <div className="checkbox-item">
                                            <Checkbox checked={pushNotif} onChange={() => setPushNotif(!pushNotif)} />
                                            <span>Send push notifications when published</span>
                                        </div>
                                    </div>
                                    {pushNotif && (
                                        <>
                                            <div>
                                                <div className="label-with-icon">
                                                    <label>Title</label>
                                                    <Tooltip
                                                        title="Enter a title for the push notification which will be sent out to users devices."
                                                        overlayInnerStyle={{ backgroundColor: '#00AEEF', color: 'white', width: '300px' }}
                                                        mouseEnterDelay={0.1}
                                                        mouseLeaveDelay={0.1}
                                                        trigger="hover">
                                                        <span className="info-icon">
                                                            <ExclamationIcon w={25} h={25} style={{ verticalAlign: 'middle', position: 'relative', top: '-1px' }} />
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                                <Input
                                                    value={pushNotifTitle}
                                                    onChange={e => setPushNotifTitle(e.target.value)} />
                                            </div>
                                            <div>
                                                <div className="label-with-icon">
                                                    <label>Push notification description</label>
                                                    <Tooltip
                                                        title="Enter a description of the push notification which will be sent out to users devices."
                                                        overlayInnerStyle={{ backgroundColor: '#00AEEF', color: 'white', width: '300px' }}
                                                        mouseEnterDelay={0.1}
                                                        mouseLeaveDelay={0.1}
                                                        trigger="hover">
                                                        <span className="info-icon">
                                                            <ExclamationIcon w={25} h={25} style={{ verticalAlign: 'middle', position: 'relative', top: '-1px' }} />
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                                <TextArea
                                                    rows={3}
                                                    value={pushNotifDescription}
                                                    onChange={e => setPushNotifDescription(e.target.value)} />
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                            {!isScheduled && (
                                <div style={{ marginTop: '20px' }}>
                                    <p>{selectedRecord.push_notification === 'yes' ? 'Yes' : 'No'} Push Notification</p>
                                    <p>{getAudienceText(selectedRecord)}</p>
                                    {selectedRecord.care_homes && selectedRecord.care_homes.length > 0 && (
                                        <div>
                                            <p><strong>Care Homes:</strong></p>
                                            <ul>
                                                {selectedRecord.care_homes.map(home => (
                                                    <li key={home.id}>{home.title}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                            {!isScheduled && (
                                <div className="scheduled-info">
                                    <ClockCircleOutlined style={{ color: '#0079C1', marginRight: 8, fontSize: '20px' }} />
                                    <span style={{ fontSize: '14px', fontFamily: 'Roboto', fontWeight: 600 }}>Post Published:</span> {selectedRecord.publish_at || selectedRecord.created_at} {isScheduled ? 'AM' : 'PM'}
                                </div>
                            )}
                            {isScheduled && (
                                <div className="builder-footer">
                                    <div className="scheduled-info">
                                        <ClockCircleOutlined style={{ color: '#0079C1', marginRight: 8, fontSize: '20px' }} />
                                        <span style={{ fontSize: '14px', fontFamily: 'Roboto', fontWeight: 600 }}>Post Scheduled:</span> {selectedRecord.publish_at || selectedRecord.created_at}
                                    </div>
                                    <Button
                                        className="primary"
                                        onClick={handlePublishNow}
                                        loading={isPublishing}
                                        disabled={isUpdating || isPublishing}>
                                        Publish Now
                                    </Button>
                                    <Button
                                        className="secondary"
                                        onClick={handleScheduleForLater}
                                        loading={isUpdating}
                                        disabled={isUpdating || isPublishing}>
                                        Schedule for Later
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className="builder-preview">
                            <PhonePreview
                                imageUrl={selectedRecord.media_url || ''}
                                title={isScheduled ? editTitle : selectedRecord.title}
                                content={isScheduled ? editContent : selectedRecord.text}
                                link={isScheduled ? editLink : (selectedRecord.link || '')}
                                linkLabel={isScheduled ? editLinkLabel : (selectedRecord.link_label || '')} />
                        </div>
                    </div>
                )}
            </Modal>
            <ScheduleModal
                visible={scheduleModalVisible}
                date={scheduleDate}
                time={scheduleTime}
                onDateChange={setScheduleDate}
                onTimeChange={setScheduleTime}
                onSave={handleScheduleSave}
                onCancel={handleScheduleCancel} />
            <Modal
                title={`Delete ${isScheduled ? 'Scheduled' : 'Published'} News Update`}
                open={isDeleteModalVisible}
                footer={[
                    <Button
                        key="delete"
                        type="primary"
                        danger
                        onClick={handleDelete}
                        loading={isFetching}
                        disabled={isFetching}
                        style={{ width: '230px', borderRadius: '11px', height: '45px', backgroundColor: '#0079C1' }}>
                        Delete
                    </Button>,
                    <Button
                        key="cancel"
                        onClick={() => setIsDeleteModalVisible(false)}
                        disabled={isFetching}
                        style={{ width: '230px', borderRadius: '11px', height: '45px', borderColor: '#0079C1', color: '#0079C1' }}>
                        Cancel
                    </Button>,
                ]}
                onCancel={() => setIsDeleteModalVisible(false)}>
                <p>
                    Delete this {isScheduled ? 'scheduled' : 'published'} update will permanently delete any record of it. Are you sure you want to delete the post?
                </p>
            </Modal>
            <Modal
                title="Confirm Publish"
                open={isPublishConfirmVisible}
                onCancel={() => setIsPublishConfirmVisible(false)}
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => setIsPublishConfirmVisible(false)}
                        disabled={isPublishing}
                        style={{ borderRadius: '8px', height: '40px' }}>
                        Cancel
                    </Button>,
                    <Button
                        key="publish"
                        type="primary"
                        onClick={handleConfirmPublish}
                        loading={isPublishing}
                        disabled={isPublishing}
                        style={{
                            borderRadius: '8px',
                            height: '40px',
                            backgroundColor: '#0079C1',
                            borderColor: '#0079C1',
                        }}>
                        Publish Now
                    </Button>,
                ]}
                maskClosable={false}
                centered
                width={450}>
                <p style={{ fontSize: '15px', fontFamily: 'Roboto', margin: 0 }}>
                    This action will immediately publish the news update with any changes you have made.
                    The article will be sent to the selected care homes and cannot be undone.
                    Are you sure you want to proceed?
                </p>
            </Modal>
        </div>
    );
};

export default NewsUpdatesTable;
